import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Formik, FormikActions } from 'formik';

import { StepperStep } from '@ui-lib/components/Stepper';
import { useSnackbarContext } from '@ui-lib/components/Snackbar/SnackbarProvider';

import HttpError from '@web-shared/services/http-service/HttpError';

import { CreateBolParams } from '../../api/types/CreateBolParams';
import { PHOTO_TYPE } from '../../api/types/PhotoType';
import { createBol } from '../../api';

import { useAppSelector } from '../../redux/types';
import { CreateBolFormValues, getValidationSchema, initValues } from './const';
import { parseErrors } from './utils';
import { StepLayout } from './components/StepLayout';

export const BolCreatePage = () => {
  const { t } = useTranslation();
  const { showErrorSnackbar } = useSnackbarContext();
  const history = useHistory();

  const [activeStep, setActiveStep] = useState(0);
  const steps: StepperStep[] = [
    { label: t('stepCounter', { value: 1 }) },
    { label: t('stepCounter', { value: 2 }) },
    { label: t('stepCounter', { value: 3 }) },
  ];

  const companyId = useAppSelector<number>((state) => state.userCompanies.companies[0]?.id);

  const onSubmit = async (
    values: CreateBolFormValues,
    { setSubmitting, setErrors }: FormikActions<CreateBolFormValues>
  ) => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
      setSubmitting(false);
    } else {
      try {
        const params: CreateBolParams = {
          company_id: companyId,
          reference_number: values.reference_number,
          token: values.token,
          lm_location_id: values.location!.id,
          ebol_photos: values.photos.map(photo => ({
            photo_type: PHOTO_TYPE.ORIGINAL,
            picture: photo,
          })),
        };
        await createBol(params);
        history.replace('/create/success');
      } catch (err) {
        setSubmitting(false);
        if (HttpError.isHttpError(err)) {
          const errorsResponse = parseErrors(err);
          setActiveStep(errorsResponse.activeStep);
          showErrorSnackbar({ message: err.message });
          // additional delay to ensure the step change is reflected before setting errors
          setTimeout(() => {
            setErrors(errorsResponse.errors);
          }, 100);
        }
      }
    }
  };

  const goBack = () => {
    setActiveStep(value => value - 1);
  };

  return (
    <>
      <Formik
        validationSchema={getValidationSchema(activeStep)}
        initialValues={initValues}
        onSubmit={onSubmit}
      >
        <StepLayout
          activeStep={activeStep}
          steps={steps}
          goBack={goBack}
        />
      </Formik>
    </>
  );
};

